/**
 * PlotCard — the standardized Quick Study plot shell (grown from `QsCardChrome`).
 *
 * Every quant insight card follows ONE grammar:
 *   header   — title + subtitle on the left; controls right-aligned. In order:
 *              [plotSwitch][metricToggle], then a hover-reveal cluster (the settings-popover
 *              trigger + the export toolbar, download → copy) that fades in on card hover, and
 *              finally [viewToggle] pinned to the extreme right edge of the card;
 *   children — exactly one opinionated exhibit (the ONLY node inside the capture seam);
 *   footer   — the tested claim (caption) + a single base-size footnote.
 *
 * The header shows the CONSTRUCT (title) + scale anchors/metric (subtitle) ONLY — never
 * the literal question/prompt text and never a base-size "n=…" line. The backend's
 * SurveyJS `question.title` is the LLM-facing prompt, not respondent-facing copy;
 * rendering it verbatim leaks internal prompt boilerplate into the UI. Base-size honesty
 * lives in the footer (`footnote`), so a header n-line is always a duplicate.
 *
 * Two rendering modes, ONE contract:
 *   - NEW API (`title`/`subtitle`/`segmentControl`/`plotSwitch`/`metricToggle`/
 *     `enableImageExport`, …) → the reference-clean, tokens-only look: calm `<Heading>`/
 *     `<Text>` type, always-on `border-border` + `shadow-elevated` frame, hover export toolbar.
 *   - LEGACY API (`construct`/`anchorsLine`/`control`) → the original `QsCardChrome`
 *     markup, byte-identical (same `qs-card-chrome-*` testids and classes) so existing
 *     callers (DivergingStackedRating, the report surface) render EXACTLY as before. The
 *     Wave-4 convergence migrates those callers to the new API and drops this branch.
 *
 * Purely presentational and props-driven — no data fetching, no page state.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Copy, Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { copyPlotImage, downloadPlotImage } from './plotImageExport'

/**
 * R10 — the ONE-LINE footer grammar every quant/qual card composes to:
 *   "Base: {base}[ · {values/metric}][ · {caveat}]"
 * `base` is the n, phrased for the card ("40 twins per page, isolated reads" for a
 * monadic read; "40 twins" pooled); any monadic isolation caveat compresses INTO that
 * base fragment rather than a separate sentence. Extra fragments (values/metric
 * definition, an honest-reading caveat) are optional and join with " · ". Falsy/blank
 * fragments are dropped so a card that only has a base never renders a trailing " · ".
 * Returns "" when `base` itself is blank (nothing groundable to state).
 */
export function buildCardFootnote(
  base: string | undefined | null,
  ...extra: Array<string | undefined | null | false>
): string {
  const first = (base || '').trim()
  if (!first) return ''
  const rest = extra.map((p) => (p || '').trim()).filter(Boolean)
  return [`Base: ${first}`, ...rest].join(' · ')
}

export interface PlotCardProps {
  /** Header lead — the construct under test. `ReactNode` so the chat title can embed
   *  `ComparisonLabelText` + a grounding-withheld tooltip button. */
  title?: ReactNode
  /** Rendered as a SIBLING of the title heading (never inside it), so adornments like
   *  the low-sample icon stay out of the heading's accessible name. */
  titleAdornment?: ReactNode
  /** Secondary header line — scale anchors, metric eyebrow, etc. */
  subtitle?: ReactNode
  /** The "Compare segments" picker slot (header, right cluster). */
  segmentControl?: ReactNode
  /** The Count|% metric toggle slot (header, right cluster). */
  metricToggle?: ReactNode
  /** The View-as plot switch slot (header, right cluster). */
  plotSwitch?: ReactNode
  /** View selector for cards whose exhibit has multiple views, e.g. open-end
   *  Themes|Quotes; rendered with the other header controls, never captured. */
  viewToggle?: ReactNode
  /** Tested-claim caption content (sig claims). Rendered above the footnote. */
  caption?: ReactNode
  /** Base-size honesty footnote — ONE line, built via `buildCardFootnote`. */
  footnote?: string
  /** When set, renders the hover export toolbar (download → copy) over the exhibit. */
  enableImageExport?: boolean
  /** Filename for the downloaded PNG (falls back to `quick-study-plot.png`). */
  exportFilename?: string
  /** Overrides the root testid (defaults to `qs-plot-card`). */
  'data-testid'?: string
  className?: string
  children: ReactNode

  // ---- Legacy aliases (map to the new slots; keep old callers compiling) ----
  /** @deprecated use `title`. */
  construct?: string
  /** @deprecated use `subtitle`. */
  anchorsLine?: string
  /** @deprecated use `segmentControl`. */
  control?: ReactNode
}

const DEFAULT_EXPORT_FILENAME = 'quick-study-plot.png'

export function PlotCard(props: PlotCardProps) {
  const {
    title,
    titleAdornment,
    subtitle,
    segmentControl,
    metricToggle,
    plotSwitch,
    viewToggle,
    caption,
    footnote,
    enableImageExport,
    exportFilename,
    className,
    children,
    construct,
    anchorsLine,
    control,
  } = props

  const dataTestId = props['data-testid']

  const captureRef = useRef<HTMLDivElement>(null)
  // T1 (Fable review) — export state is LIFTED here from `ExportToolbar` so the whole header
  // controls cluster (plotSwitch/metricToggle/segmentControl/viewToggle + the toolbar) can be
  // frozen while a raster is in flight. `rasterizePlot` reads the live DOM after two awaits
  // (lazy html-to-image + `document.fonts.ready`); if the marketer flips the viewToggle mid-
  // export the capture grabs the WRONG view (violating the themes-only export invariant). The
  // buttons were already disabled, but the surrounding controls stayed clickable — so we make
  // the cluster inert (`pointer-events-none` + `aria-disabled`) for the duration.
  const [isExporting, setIsExporting] = useState(false)

  const usesLegacyApi = construct !== undefined || anchorsLine !== undefined || control !== undefined
  const usesNewApi =
    title !== undefined ||
    titleAdornment !== undefined ||
    subtitle !== undefined ||
    segmentControl !== undefined ||
    metricToggle !== undefined ||
    plotSwitch !== undefined ||
    viewToggle !== undefined ||
    enableImageExport === true

  // Legacy callers (DivergingStackedRating, report surface) render the original chrome
  // byte-identically — zero behavioral/visual change until the Wave-4 convergence.
  if (usesLegacyApi && !usesNewApi) {
    return (
      <LegacyChrome
        construct={construct}
        anchorsLine={anchorsLine}
        control={control}
        caption={caption}
        footnote={footnote}
        className={className}
      >
        {children}
      </LegacyChrome>
    )
  }

  // ---- New reference-clean render ----
  const resolvedTitle = title ?? construct
  const resolvedSubtitle = subtitle ?? anchorsLine
  const resolvedSegmentControl = segmentControl ?? control

  const showToolbar = Boolean(enableImageExport)
  // The toolbar alone must never create an (otherwise empty) header band — when nothing
  // real renders in the header, the toolbar falls back to an absolute overlay instead.
  const hasRealControls = Boolean(plotSwitch || metricToggle || resolvedSegmentControl || viewToggle)
  const hasHeader = Boolean(resolvedTitle || titleAdornment || resolvedSubtitle || hasRealControls)
  const showToolbarInHeader = showToolbar && hasHeader
  const showToolbarOverlay = showToolbar && !hasHeader
  const hasFooter = Boolean(caption || footnote)

  return (
    <section
      className={cn(
        'group/plotcard relative isolate w-full space-y-3 px-0 py-4',
        "after:pointer-events-none after:absolute after:inset-y-0 after:-left-5 after:-right-5 after:z-10 after:content-[''] after:rounded-lg after:border after:border-border after:shadow-elevated",
        className,
      )}
      data-testid={dataTestId ?? 'qs-plot-card'}
    >
      {hasHeader && (
        <header className="flex items-start justify-between gap-3" data-testid="qs-plot-card-header">
          <div className="min-w-0 space-y-0.5">
            {(resolvedTitle || titleAdornment) && (
              <div className="flex items-start gap-2">
                {resolvedTitle && (
                  <Heading
                    level={5}
                    size="sm"
                    weight="medium"
                    className="font-sans text-foreground"
                    data-testid="qs-plot-card-title"
                  >
                    {resolvedTitle}
                  </Heading>
                )}
                {titleAdornment}
              </div>
            )}
            {resolvedSubtitle && (
              <Text as="p" size="xs" color="muted" className="leading-snug" data-testid="qs-plot-card-subtitle">
                {resolvedSubtitle}
              </Text>
            )}
          </div>
          {(hasRealControls || showToolbarInHeader) && (
            <div
              className={cn(
                'flex min-w-0 flex-wrap items-center justify-end gap-2',
                // Freeze the entire control cluster while exporting so no header control (above
                // all the viewToggle) can change the captured view mid-raster.
                isExporting && 'pointer-events-none',
              )}
              aria-disabled={isExporting || undefined}
              data-testid="qs-plot-card-controls"
            >
              {plotSwitch}
              {metricToggle}
              {/* Hover-actions cluster — the settings popover trigger + the export toolbar
                  (download → copy) reveal together on card hover, sitting just LEFT of the
                  view toggle. The settings trigger self-manages its reveal (it pins open) via
                  `revealOnHover`; the toolbar owns its own. */}
              {(resolvedSegmentControl || showToolbarInHeader) && (
                <div className="flex items-center gap-1" data-testid="qs-plot-card-hover-actions">
                  {resolvedSegmentControl}
                  {showToolbarInHeader && (
                    <ExportToolbar
                      captureRef={captureRef}
                      exportFilename={exportFilename}
                      onPendingChange={setIsExporting}
                    />
                  )}
                </div>
              )}
              {/* The view toggle is pinned to the extreme right edge of the card header. */}
              {viewToggle}
            </div>
          )}
        </header>
      )}

      <div className="relative" data-testid="qs-plot-card-exhibit">
        {showToolbarOverlay && (
          <div className="absolute right-0 top-0 z-10">
            <ExportToolbar captureRef={captureRef} exportFilename={exportFilename} />
          </div>
        )}
        <div ref={captureRef} data-testid="qs-plot-card-capture">
          {children}
        </div>
      </div>

      {hasFooter && (
        <footer className="space-y-1" data-testid="qs-plot-card-caption">
          {caption}
          {footnote && (
            <Text as="p" size="xs" color="muted" className="leading-snug" data-testid="qs-plot-card-footnote">
              {footnote}
            </Text>
          )}
        </footer>
      )}
    </section>
  )
}

interface ExportToolbarProps {
  captureRef: React.RefObject<HTMLElement>
  exportFilename?: string
  /** T1 — notifies the parent PlotCard when an export is in flight so it can freeze the whole
   *  header controls cluster (not just these buttons) for the duration. */
  onPendingChange?: (busy: boolean) => void
}

/**
 * Hover-revealed export toolbar — lives in the header's hover-actions cluster, immediately
 * after the settings-popover trigger and just LEFT of the view toggle: download FIRST, then
 * copy (per the reference order). `plotImageExport` is imported statically — it is a tiny module
 * that lazy-loads `html-to-image` internally, so `navigator.clipboard.write` is
 * reached synchronously within the click gesture (Safari requirement) while the
 * heavy raster lib stays out of the main bundle. The utils toast on success and
 * THROW on failure; the toolbar owns the error toast and the copied-check state —
 * it must never throw into the card.
 */
function ExportToolbar(props: ExportToolbarProps) {
  const { captureRef, exportFilename, onPendingChange } = props

  const { t } = useTranslation('app')
  const [isCopied, setIsCopied] = useState(false)
  // Per-action pending state — the raster (lazy html-to-image, `document.fonts.ready`,
  // 2x) is multi-second on large cards. While an export is in flight we swap the button's
  // icon for a spinner, disable BOTH buttons, and pin the toolbar visible.
  const [pendingAction, setPendingAction] = useState<'download' | 'copy' | null>(null)

  const isPending = pendingAction !== null

  // T1 — surface the in-flight state up to PlotCard so it can freeze the header controls. An
  // effect (rather than firing inside the handlers) keeps the Safari clipboard gesture intact:
  // no promise/await is introduced before `copyPlotImage` in `handleCopy`.
  useEffect(() => {
    onPendingChange?.(isPending)
  }, [isPending, onPendingChange])

  const handleDownload = useCallback(async () => {
    const node = captureRef.current
    if (!node) return
    setPendingAction('download')
    try {
      await downloadPlotImage(node as HTMLElement, exportFilename ?? DEFAULT_EXPORT_FILENAME)
    } catch (error) {
      console.error('PlotCard/handleDownload - error', error)
      toast.error(t('quickStudyChrome.plotDownloadFailed'))
    } finally {
      setPendingAction(null)
    }
  }, [captureRef, exportFilename, t])

  const handleCopy = useCallback(async () => {
    const node = captureRef.current
    if (!node) return
    // Safari constraint: `navigator.clipboard.write` must be reached synchronously within
    // the click gesture. Setting React state is synchronous and safe here; `copyPlotImage`
    // stays the FIRST await so no promise is introduced before the clipboard write.
    setPendingAction('copy')
    try {
      await copyPlotImage(node as HTMLElement)
      setIsCopied(true)
      window.setTimeout(() => setIsCopied(false), 2000)
    } catch (error) {
      console.error('PlotCard/handleCopy - error', error)
      toast.error(t('quickStudyChrome.plotCopyFailed'))
    } finally {
      setPendingAction(null)
    }
  }, [captureRef, t])

  return (
    <div
      className={cn(
        'flex items-center gap-1',
        'opacity-0 transition-opacity focus-within:opacity-100 group-hover/plotcard:opacity-100',
        // Pin the toolbar visible while exporting so the hover-reveal can't fade the spinner
        // out (tailwind-merge lets the later opacity-100 win over opacity-0).
        isPending && 'opacity-100',
      )}
      data-capture-exclude
    >
      <button
        type="button"
        onClick={handleDownload}
        disabled={isPending}
        aria-busy={pendingAction === 'download' || undefined}
        aria-label={t('quickStudyChrome.downloadPlotImage')}
        className={cn(
          'inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground',
          'transition-smooth hover:bg-accent hover:text-accent-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:pointer-events-none disabled:opacity-50',
        )}
        data-testid="qs-plot-download"
      >
        {pendingAction === 'download' ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Download className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
      <button
        type="button"
        onClick={handleCopy}
        disabled={isPending}
        aria-busy={pendingAction === 'copy' || undefined}
        aria-label={t('quickStudyChrome.copyPlotImage')}
        className={cn(
          'inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground',
          'transition-smooth hover:bg-accent hover:text-accent-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:pointer-events-none disabled:opacity-50',
        )}
        data-testid="qs-plot-copy"
      >
        {pendingAction === 'copy' ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : isCopied ? (
          <Check className="h-4 w-4 text-chart-positive" aria-hidden="true" />
        ) : (
          <Copy className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
    </div>
  )
}

interface LegacyChromeProps {
  construct?: string
  anchorsLine?: string
  control?: ReactNode
  caption?: ReactNode
  footnote?: string
  className?: string
  children: ReactNode
}

/**
 * The original `QsCardChrome` markup, preserved byte-identically (same `qs-card-chrome-*`
 * testids + classes) so legacy callers and the report surface render EXACTLY as before.
 * Removed in the Wave-4 convergence once every caller uses the new API.
 */
function LegacyChrome(props: LegacyChromeProps) {
  const { construct, anchorsLine, control, caption, footnote, className, children } = props

  const metaLine = (anchorsLine ?? '').trim()
  const hasHeader = Boolean(construct || metaLine || control)

  return (
    <section className={cn('w-full space-y-3', className)} data-testid="qs-card-chrome">
      {hasHeader && (
        <header className="flex items-start justify-between gap-3" data-testid="qs-card-chrome-header">
          <div className="min-w-0 space-y-0.5">
            {construct && (
              <p className="text-sm font-semibold text-twin-chat-fg" data-testid="qs-card-chrome-construct">
                {construct}
              </p>
            )}
            {metaLine && (
              <p className="text-[11px] text-twin-chat-fg-muted" data-testid="qs-card-chrome-meta">
                {metaLine}
              </p>
            )}
          </div>
          {control && <div className="shrink-0">{control}</div>}
        </header>
      )}
      <div data-testid="qs-card-chrome-exhibit">{children}</div>
      {(caption || footnote) && (
        <footer className="space-y-1" data-testid="qs-card-chrome-caption">
          {caption}
          {footnote && (
            <p className="text-[11px] text-twin-chat-fg-muted" data-testid="qs-card-chrome-footnote">
              {footnote}
            </p>
          )}
        </footer>
      )}
    </section>
  )
}
